<template>
  <div>
    <Head :title="title" />
    <div class="flex flex-wrap gap-6">
      <div class="bg-white rounded-md shadow overflow-hidden max-w-3xl flex-1">
        <form @submit.prevent="update">
          <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
            <text-input v-model="form.name" :error="form.errors.name" class="pr-6 pb-8 w-full" label="Nombre" />

            <div class="pr-6 pb-8 w-full">
              <icon-picker v-model="form.icon" />
            </div>

            <div class="pr-6 pb-8 w-full">
              <type-selector v-model="form.type_ids" :equipment-types="equipment_types" :location-types="location_types" />
            </div>
          </div>

          <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center">
            <loading-button :loading="form.processing" class="btn-indigo" type="submit">Actualizar clasificación</loading-button>
          </div>
        </form>
      </div>

      <div class="bg-white rounded-md shadow overflow-hidden w-full lg:w-96 p-8">
        <h3 class="font-bold mb-1">Subclasificaciones</h3>
        <p class="text-sm text-gray-500 mb-4">El personal de soporte elige una de estas al completar un ticket registrado con esta clasificación.</p>

        <ul class="mb-6">
          <li v-for="sub in subclassifications" :key="sub.id" class="py-2 border-b">
            <div class="flex items-center justify-between">
              <span class="text-sm flex flex-col">
                <span class="flex items-center gap-2">
                  <icon v-if="sub.icon" :name="sub.icon" class="w-4 h-4 fill-gray-500" />
                  {{ sub.name }}
                </span>
                <span class="text-xs text-gray-400">{{ sub.priority || 'Sin prioridad' }}</span>
              </span>
              <div class="flex items-center gap-3">
                <button type="button" class="text-indigo-600 text-xs hover:underline" @click="toggleEdit(sub)">Editar</button>
                <button type="button" class="text-red-600 text-xs hover:underline" @click="removeSubclassification(sub)">Quitar</button>
              </div>
            </div>
            <div v-if="editingFor === sub.id" class="mt-2">
              <select-input v-model="editingPriority" class="w-full mb-3" label="Prioridad automática">
                <option :value="null">Sin prioridad</option>
                <option v-for="p in priorities" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select-input>
              <icon-picker v-model="editingIcon" />
              <div class="mt-3">
                <type-selector v-model="editingTypeIds" :equipment-types="equipment_types" :location-types="location_types" />
              </div>
              <div class="flex gap-2 mt-2">
                <button type="button" class="btn-indigo flex-1 text-sm" :disabled="savingSubclassification" @click="saveSubclassification(sub)">Guardar</button>
                <button type="button" class="flex-1 text-sm border rounded" @click="editingFor = null">Cancelar</button>
              </div>
            </div>
          </li>
          <li v-if="subclassifications.length === 0" class="text-sm text-gray-400 py-2">Sin subclasificaciones.</li>
        </ul>

        <div class="border-t pt-4">
          <text-input v-model="newSubclassification.name" class="w-full mb-3" label="Agregar subclasificación" />
          <select-input v-model="newSubclassification.priority_id" class="w-full mb-3" label="Prioridad automática">
            <option :value="null">Sin prioridad</option>
            <option v-for="p in priorities" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select-input>
          <icon-picker v-model="newSubclassification.icon" />
          <div class="mt-3">
            <type-selector v-model="newSubclassification.type_ids" :equipment-types="equipment_types" :location-types="location_types" />
          </div>
          <button type="button" class="btn-indigo w-full mt-3" :disabled="!newSubclassification.name || addingSubclassification" @click="addSubclassification">Agregar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import IconPicker from '@/Shared/IconPicker'
import TypeSelector from '@/Shared/TypeSelector'
import LoadingButton from '@/Shared/LoadingButton'
import { Head } from '@inertiajs/inertia-vue3'

export default {
  metaInfo() {
    return { title: this.form.name }
  },
  components: {
    LoadingButton,
    TextInput,
    SelectInput,
    IconPicker,
    TypeSelector,
    Icon,
    Head,
  },
  layout: Layout,
  props: {
    title: String,
    classification: Object,
    subclassifications: Array,
    priorities: Array,
    equipment_types: Array,
    location_types: Array,
  },
  remember: 'form',
  data() {
    return {
      newSubclassification: {
        name: null,
        icon: null,
        priority_id: null,
        type_ids: [],
      },
      editingFor: null,
      editingIcon: null,
      editingPriority: null,
      editingTypeIds: [],
      addingSubclassification: false,
      savingSubclassification: false,
      form: this.$inertia.form({
        name: this.classification.name,
        icon: this.classification.icon,
        type_ids: this.classification.type_ids || [],
      }),
    }
  },
  methods: {
    update() {
      this.form.put(this.route('classifications.update', this.classification.id))
    },
    addSubclassification() {
      if (this.addingSubclassification) {
        return
      }
      this.addingSubclassification = true
      this.$inertia.post(this.route('classifications.subclassifications.store', this.classification.id), {
        name: this.newSubclassification.name,
        icon: this.newSubclassification.icon,
        priority_id: this.newSubclassification.priority_id,
        type_ids: this.newSubclassification.type_ids,
      }, {
        preserveScroll: true,
        onSuccess: () => {
          this.newSubclassification = { name: null, icon: null, priority_id: null, type_ids: [] }
        },
        onFinish: () => {
          this.addingSubclassification = false
        },
      })
    },
    removeSubclassification(sub) {
      if (confirm('¿Quitar la subclasificación "' + sub.name + '"?')) {
        this.$inertia.delete(this.route('classifications.subclassifications.destroy', sub.id), { preserveScroll: true })
      }
    },
    toggleEdit(sub) {
      if (this.editingFor === sub.id) {
        this.editingFor = null
        return
      }
      this.editingFor = sub.id
      this.editingIcon = sub.icon
      this.editingPriority = sub.priority_id
      this.editingTypeIds = sub.type_ids || []
    },
    saveSubclassification(sub) {
      if (this.savingSubclassification) {
        return
      }
      this.savingSubclassification = true
      this.$inertia.put(this.route('classifications.subclassifications.update', sub.id), {
        name: sub.name,
        icon: this.editingIcon,
        priority_id: this.editingPriority,
        type_ids: this.editingTypeIds,
      }, {
        preserveScroll: true,
        onSuccess: () => {
          this.editingFor = null
        },
        onFinish: () => {
          this.savingSubclassification = false
        },
      })
    },
  },
}
</script>
